// launched.tsx — post-launch confirmation surface for the Drip Campaigns
// redesign. Renders inside an ancestor `.campaigns-rd` wrapper (supplies the
// design tokens).
//
// ROUTE-DRIVEN: this is the `/campaigns/:id/launched` route (replacing the old
// in-wizard `launched` local-state flag). It loads the launched drip by id so a
// reload of the URL still renders the right copy.
//
// WHAT THIS SCREEN IS FOR: it is the only place a person is told what they just
// committed to, at the one moment they can still act on being surprised. It
// used to get that wrong in three ways, all of them by asserting rather than
// reading:
//
//   • it promised "Follow-ups … stop for anyone who replies" UNCONDITIONALLY.
//     It never read `drip.stopOnReply`. With the toggle off — the wizard's own
//     copy for which is "Everyone gets all the follow-ups, even after they
//     reply" — the confirmation screen stated the opposite of the truth.
//   • the Review step promises "Repeats <cadence>", and this screen dropped it,
//     so someone who set a weekly repeat got no confirmation the repeat took.
//   • every string was a hardcoded English literal (0 of 8 through `t`), on the
//     one screen in the flow with no second chance to re-read it later.
//
// The facts are now a short labelled list rather than one run-on sentence: each
// row is one mark and one line, and each row is READ off the drip.

import React from 'react';
import { Layers, Clock, Repeat, Reply, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { dripsSelectOneObjectByQuery, DBDripThunks } from '@common-reducers/DBServiceThunks';
import type { Drip } from '@bt-common/schemas/bt/drip';
import { LaunchCheck } from './launch-check';
import { recurrenceSummary } from './campaign-recurrence-summary';

export interface CampaignLaunchedProps {
  /** The launched drip id (from the route). Everything else is read off it. */
  dripId: string;
  onView: () => void;
  onAnother: () => void;
}

/** One fact about what was just launched: a mark, and one line that says it. */
const Fact: React.FC<{ icon: React.ElementType; children: React.ReactNode }> = ({ icon: Icon, children }) => (
  <li style={{ display: 'flex', alignItems: 'flex-start', gap: 9, textAlign: 'start' }}>
    <Icon size={14} style={{ flex: 'none', marginTop: 2, color: 'var(--muted-2)' }} />
    <span style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>{children}</span>
  </li>
);

export const CampaignLaunched: React.FC<CampaignLaunchedProps> = ({ dripId, onView, onAnother }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const drip = useAppSelector((s) => dripsSelectOneObjectByQuery(s.DBDripReducer, { _id: dripId })) as Drip | undefined;
  React.useEffect(() => {
    dispatch(DBDripThunks.find({ _id: dripId }));
  }, [dispatch, dripId]);

  const stepCount = drip?.steps?.length ?? 1;
  const multi = stepCount > 1;
  // Read, never assumed — this is the claim that used to be false.
  const stopsOnReply = !!drip?.stopOnReply;

  // When the campaign was scheduled for later, surface the exact start
  // date/time instead of the "sending now" copy. Guard on `isTimeValid` — a
  // "start now" launch persists no meaningful dueDate.
  const scheduledAt =
    drip?.schedule?.isTimeValid && drip?.schedule?.dueDate ? new Date(drip.schedule.dueDate) : null;
  const scheduledLabel =
    scheduledAt && !isNaN(scheduledAt.getTime())
      ? scheduledAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
      : null;

  const repeats = recurrenceSummary(drip?.schedule?.rruleset, t);

  return (
    <div className="col" style={{ flex: 1, background: 'var(--bg)', alignItems: 'center', justifyContent: 'center' }}>
      <div className="fade-in" style={{ maxWidth: 460, textAlign: 'center', padding: 40 }}>
        <div className="pop" style={{ width: 120, height: 120, margin: '0 auto' }}>
          <LaunchCheck size={120} />
        </div>
        <h1 style={{ fontSize: 23, fontWeight: 600, marginTop: 8, letterSpacing: '-0.02em' }}>
          {scheduledLabel
            ? t('campaignsRedesign.launched.titleScheduled', 'Your campaign is scheduled')
            : t('campaignsRedesign.launched.titleSending', 'Your campaign is on its way')}
        </h1>

        {/* Every row is read off the drip. The list only ever states things that
            are true of THIS campaign — a row it cannot substantiate is absent
            rather than softened. */}
        <ul style={{ listStyle: 'none', margin: '18px 0 0', padding: 0, display: 'grid', gap: 9 }}>
          <Fact icon={scheduledLabel ? Clock : Send}>
            {scheduledLabel
              ? (multi
                  ? t('campaignsRedesign.launched.startsFirstAt', 'The first message starts sending on {{when}}.', { when: scheduledLabel })
                  : t('campaignsRedesign.launched.startsAt', 'It starts sending on {{when}}.', { when: scheduledLabel }))
              : (multi
                  ? t('campaignsRedesign.launched.sendingFirstNow', 'The first message is sending now.')
                  : t('campaignsRedesign.launched.sendingNow', 'It is sending now.'))}
          </Fact>

          {multi && (
            <Fact icon={Layers}>
              {t('campaignsRedesign.launched.followUpCount', '{{count}} follow-ups go out after it, on the waits you set.', { count: stepCount - 1 })}
            </Fact>
          )}

          {multi && (
            <Fact icon={Reply}>
              {stopsOnReply
                ? t('campaignsRedesign.launched.stopsOnReply', 'Anyone who replies stops getting the follow-ups.')
                : t('campaignsRedesign.launched.noStopOnReply', 'Everyone gets every follow-up, even after they reply.')}
            </Fact>
          )}

          {repeats && (
            <Fact icon={Repeat}>
              {t('campaignsRedesign.launched.repeats', 'It runs again {{cadence}}.', { cadence: repeats })}
            </Fact>
          )}
        </ul>

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'center' }}>
          <button type="button" className="btn primary lg" onClick={onView}>
            <Layers size={15} /> {t('campaignsRedesign.launched.track', 'Track progress')}
          </button>
          <button type="button" className="btn lg" onClick={onAnother}>
            {t('campaignsRedesign.launched.another', 'Create another campaign')}
          </button>
        </div>
      </div>
    </div>
  );
};
